/**
 * SecureEmbed Unified Organisations + CRM API Factory
 * Version: 14.25.0
 *
 * Shared API client factory — each portal passes its own axios instance
 * (which already has the correct auth token interceptor).
 *
 * The organizations API now has its own standalone API Gateway.
 * Portal configuration should set the axios baseURL to the organizations
 * API endpoint (from SSM: /{project}/{env}/organizations/api_url).
 */

import type { AxiosInstance } from 'axios';

export interface OrgApiConfig {
  apiClient: AxiosInstance;
}

export function createOrgApi(config: OrgApiConfig) {
  const { apiClient } = config;

  return {
    // --- Organisations ---
    listOrganisations: async (params?: Record<string, any>): Promise<any> => {
      const response = await apiClient.get('/organisations', { params });
      return response.data;
    },
    getOrganisation: async (id: string): Promise<any> => {
      const response = await apiClient.get(`/organisations/${id}`);
      return response.data;
    },
    createOrganisation: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/organisations', data);
      return response.data;
    },
    updateOrganisation: async (id: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.put(`/organisations/${id}`, data);
      return response.data;
    },
    deleteOrganisation: async (id: string): Promise<any> => {
      const response = await apiClient.delete(`/organisations/${id}`);
      return response.data;
    },
    searchOrganisations: async (params: Record<string, any>): Promise<any> => {
      const response = await apiClient.get('/organisations/search', { params });
      return response.data;
    },
    getSimilarOrganisations: async (id: string, limit?: number): Promise<any> => {
      const response = await apiClient.get(`/organisations/${id}/similar`, {
        params: { limit: limit || 5 },
      });
      return response.data;
    },
    getOrganisationStats: async (): Promise<any> => {
      const response = await apiClient.get('/organisations/stats');
      return response.data;
    },
    searchExternal: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/organisations/search-external', data);
      return response.data;
    },

    // --- Contacts ---
    listContacts: async (): Promise<any> => {
      const response = await apiClient.get('/contacts');
      return response.data;
    },
    getContact: async (id: string): Promise<any> => {
      const response = await apiClient.get(`/contacts/${id}`);
      return response.data;
    },
    createContact: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/contacts', data);
      return response.data;
    },
    updateContact: async (id: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.put(`/contacts/${id}`, data);
      return response.data;
    },
    deleteContact: async (id: string): Promise<any> => {
      const response = await apiClient.delete(`/contacts/${id}`);
      return response.data;
    },
    linkContactToOrg: async (contactId: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post(`/contacts/${contactId}/organisations`, data);
      return response.data;
    },
    unlinkContactFromOrg: async (contactId: string, orgId: string): Promise<any> => {
      const response = await apiClient.delete(`/contacts/${contactId}/organisations/${orgId}`);
      return response.data;
    },
    getContactsByOrg: async (orgId: string): Promise<any> => {
      const response = await apiClient.get(`/contacts/by-org/${orgId}`);
      return response.data;
    },

    // --- Comments ---
    listComments: async (targetType: string, targetId: string): Promise<any> => {
      const response = await apiClient.get('/comments', {
        params: { target_type: targetType, target_id: targetId },
      });
      return response.data;
    },
    addComment: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/comments', data);
      return response.data;
    },
    updateComment: async (id: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.put(`/comments/${id}`, data);
      return response.data;
    },
    deleteComment: async (id: string): Promise<any> => {
      const response = await apiClient.delete(`/comments/${id}`);
      return response.data;
    },

    // --- Interactions ---
    listInteractions: async (targetType: string, targetId: string): Promise<any> => {
      const response = await apiClient.get('/interactions', {
        params: { target_type: targetType, target_id: targetId },
      });
      return response.data;
    },
    addInteraction: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/interactions', data);
      return response.data;
    },
    updateInteraction: async (id: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.put(`/interactions/${id}`, data);
      return response.data;
    },
    deleteInteraction: async (id: string): Promise<any> => {
      const response = await apiClient.delete(`/interactions/${id}`);
      return response.data;
    },

    // --- Documents ---
    listDocuments: async (targetType: string, targetId: string): Promise<any> => {
      const response = await apiClient.get('/documents', {
        params: { target_type: targetType, target_id: targetId },
      });
      return response.data;
    },
    getUploadUrl: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/documents/upload', data);
      return response.data;
    },
    updateDocument: async (id: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.put(`/documents/${id}`, data);
      return response.data;
    },
    deleteDocument: async (id: string): Promise<any> => {
      const response = await apiClient.delete(`/documents/${id}`);
      return response.data;
    },
    toggleDocVisibility: async (id: string, visible: boolean): Promise<any> => {
      const response = await apiClient.put(`/documents/${id}/visibility`, {
        embedding_visible: visible,
      });
      return response.data;
    },
    triggerEmbed: async (id: string): Promise<any> => {
      const response = await apiClient.post(`/documents/${id}/embed`);
      return response.data;
    },
    getDownloadUrl: async (id: string): Promise<any> => {
      const response = await apiClient.get(`/documents/${id}/download`);
      return response.data;
    },

    // --- Shares ---
    createShare: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/shares', data);
      return response.data;
    },
    listSharesSent: async (): Promise<any> => {
      const response = await apiClient.get('/shares/sent');
      return response.data;
    },
    listSharesReceived: async (): Promise<any> => {
      const response = await apiClient.get('/shares/received');
      return response.data;
    },
    updateShare: async (id: string, data: Record<string, any>): Promise<any> => {
      const response = await apiClient.put(`/shares/${id}`, data);
      return response.data;
    },
    revokeShare: async (id: string): Promise<any> => {
      const response = await apiClient.delete(`/shares/${id}`);
      return response.data;
    },

    // --- Connections ---
    getConnections: async (entityType: string, entityId: string): Promise<any> => {
      const response = await apiClient.get('/connections', {
        params: { entity_type: entityType, entity_id: entityId },
      });
      return response.data;
    },
    discoverConnections: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/connections/discover', data);
      return response.data;
    },

    // --- Connection Requests ---
    createConnectionRequest: async (data: Record<string, any>): Promise<any> => {
      const response = await apiClient.post('/connection-requests', data);
      return response.data;
    },
    listConnectionRequests: async (): Promise<any> => {
      const response = await apiClient.get('/connection-requests');
      return response.data;
    },
    respondToConnectionRequest: async (id: string, action: string): Promise<any> => {
      const response = await apiClient.put(`/connection-requests/${id}`, { action });
      return response.data;
    },

    // --- Audit ---
    getAuditLog: async (params?: Record<string, any>): Promise<any> => {
      const response = await apiClient.get('/audit', { params });
      return response.data;
    },
  };
}

export type OrgApi = ReturnType<typeof createOrgApi>;
